import { motion } from 'framer-motion';
import { FaUserFriends, FaCode, FaChartLine, FaGlobe } from 'react-icons/fa';
import { FaUsersGear } from 'react-icons/fa6';

export default function AboutAnimatedIcons() {
  const SPEED = 4; // Orbiting speed for dots

  return (
    <div className="w-full flex items-center justify-center overflow-hidden py-8">
      <div className="relative w-[300px] h-[300px] sm:w-[350px] sm:h-[350px] md:w-[450px] md:h-[450px] mx-auto flex items-center justify-center shrink-0">
        
        {/* Central Nucleus (Heavy Neon Glow) */}
        <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
          <div className="w-32 h-32 md:w-48 md:h-48 rounded-full border-2 border-[#38BDF8]/60 bg-[#0B1120]/80 backdrop-blur-lg shadow-[0_0_50px_rgba(56,189,248,0.5),inset_0_0_30px_rgba(56,189,248,0.3)] flex items-center justify-center relative">
            <div className="absolute inset-2 md:inset-3 rounded-full border border-[#38BDF8]/20 shadow-[inset_0_0_15px_rgba(56,189,248,0.2)]" />
            <FaUserFriends className="text-5xl md:text-7xl text-white drop-shadow-[0_0_20px_#38BDF8]" />
          </div>
        </div>

        {/* Static Neon Orbital Ring */}
        <div className="absolute inset-8 md:inset-10 z-0 pointer-events-none rounded-full border-2 border-dashed border-[#38BDF8]/30 shadow-[0_0_20px_rgba(56,189,248,0.15)]" />

        {/* Orbiting Planets (Electrons) */}
        <motion.div 
          animate={{ rotate: 360 }}
          transition={{ duration: SPEED, repeat: Infinity, ease: "linear" }}
          className="absolute inset-8 md:inset-10 z-20 pointer-events-none"
        >
          {/* Blue Planet */}
          <div className="absolute inset-0 transform rotate-[-45deg]">
             {/* Curved Beam/Tail */}
             <div 
               className="absolute inset-0 rounded-full blur-[1px] opacity-90" 
               style={{
                 background: 'conic-gradient(from 0deg, transparent 0%, transparent 60%, #38BDF8 100%)',
                 WebkitMaskImage: 'radial-gradient(closest-side, transparent calc(100% - 5px), black calc(100% - 4px))',
                 maskImage: 'radial-gradient(closest-side, transparent calc(100% - 5px), black calc(100% - 4px))'
               }} 
             />
             {/* Dot */}
             <div className="absolute top-[-4px] md:top-[-5px] left-1/2 -translate-x-1/2 w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-white shadow-[0_0_15px_4px_#38BDF8,0_0_30px_8px_#38BDF8]" />
          </div>
          {/* Yellow Planet */}
          <div className="absolute inset-0 transform rotate-[135deg]">
             {/* Curved Beam/Tail */}
             <div 
               className="absolute inset-0 rounded-full blur-[1px] opacity-90" 
               style={{
                 background: 'conic-gradient(from 0deg, transparent 0%, transparent 60%, #FAA617 100%)',
                 WebkitMaskImage: 'radial-gradient(closest-side, transparent calc(100% - 5px), black calc(100% - 4px))',
                 maskImage: 'radial-gradient(closest-side, transparent calc(100% - 5px), black calc(100% - 4px))'
               }} 
             />
             {/* Dot */}
             <div className="absolute top-[-4px] md:top-[-5px] left-1/2 -translate-x-1/2 w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-white shadow-[0_0_15px_4px_#FAA617,0_0_30px_8px_#FAA617]" />
          </div>
        </motion.div>

        {/* 4 Outer Static Icons */}
        <div className="absolute inset-8 md:inset-10 z-30 pointer-events-none">
           {/* Top Left Icon (Code) */}
           <div className="absolute top-[14.6%] left-[14.6%] -translate-x-1/2 -translate-y-1/2 pointer-events-auto hover:scale-110 transition-transform">
              <div className="w-12 h-12 md:w-20 md:h-20 bg-[#0B1120] rounded-full shadow-[0_0_30px_rgba(56,189,248,0.7),inset_0_0_15px_rgba(56,189,248,0.5)] flex items-center justify-center border border-[#38BDF8]/60">
                <FaCode className="text-xl md:text-3xl text-white drop-shadow-[0_0_12px_#38BDF8]" />
              </div>
           </div>

           {/* Top Right Icon (Management) */}
           <div className="absolute top-[14.6%] right-[14.6%] translate-x-1/2 -translate-y-1/2 pointer-events-auto hover:scale-110 transition-transform">
              <div className="w-12 h-12 md:w-20 md:h-20 bg-[#0B1120] rounded-full shadow-[0_0_30px_rgba(250,166,23,0.7),inset_0_0_15px_rgba(250,166,23,0.5)] flex items-center justify-center border border-[#FAA617]/60">
                <FaUsersGear className="text-xl md:text-3xl text-white drop-shadow-[0_0_12px_#FAA617]" />
              </div>
           </div>

           {/* Bottom Left Icon (Globe) */}
           <div className="absolute bottom-[14.6%] left-[14.6%] -translate-x-1/2 translate-y-1/2 pointer-events-auto hover:scale-110 transition-transform">
              <div className="w-12 h-12 md:w-20 md:h-20 bg-[#0B1120] rounded-full shadow-[0_0_30px_rgba(56,189,248,0.7),inset_0_0_15px_rgba(56,189,248,0.5)] flex items-center justify-center border border-[#38BDF8]/60">
                <FaGlobe className="text-xl md:text-3xl text-white drop-shadow-[0_0_12px_#38BDF8]" />
              </div>
           </div>

           {/* Bottom Right Icon (Chart) */}
           <div className="absolute bottom-[14.6%] right-[14.6%] translate-x-1/2 translate-y-1/2 pointer-events-auto hover:scale-110 transition-transform">
              <div className="w-12 h-12 md:w-20 md:h-20 bg-[#0B1120] rounded-full shadow-[0_0_30px_rgba(250,166,23,0.7),inset_0_0_15px_rgba(250,166,23,0.5)] flex items-center justify-center border border-[#FAA617]/60">
                <FaChartLine className="text-xl md:text-3xl text-white drop-shadow-[0_0_12px_#FAA617]" />
              </div>
           </div>
        </div>

      </div>
    </div>
  );
}
